//import { StoryblokRichText } from '@storyblok/react'
import html2canvas from 'html2canvas'
import jsPDF from 'jspdf'
import React, { useEffect, useMemo, useRef, useState } from 'react'

import { PhnxButton } from '@/components/01-atoms/Button/Button'

import { uploadPDFToStoryblok } from '../../../../../utilities/pdfService'
import RichText from '../../../../01-atoms/RichText/Blok'
import { IndustryData, IndustryDataEntry, ProcessedFormFields } from '../../types'
import BulletChart from '../BulletChart/BulletChart'
import styles from './Results.module.css'

// Define proper types instead of any
interface PrintData {
  [key: string]: unknown
}

type PrintDataOrReactNode = PrintData | React.ReactNode

interface Disclaimer {
  type?: string
  content?: string
  richtext?: string
  [key: string]: unknown
}

interface ResultsProps {
  isOpen: boolean
  onClose: () => void
  formData: Record<string, string>
  processedFormFields: ProcessedFormFields
  industryData: IndustryData
  ratings: Rating[]
  printData: PrintDataOrReactNode
  disclaimer: Disclaimer | React.ReactNode
  onScoresCalculated?: (scores: {
    profitability: string
    capitalManagement: string
    growthReadiness: string
  }) => void
  onDownload?: (handler: () => Promise<void>) => void
  shouldUploadToStoryblok?: boolean
}

// Define valid field keys
type ValidField =
  | 'net_operating_profit'
  | 'sales'
  | 'gross_margin'
  | 'net_operating_profit_margin'
  | 'wage_exp_percentage'
type BenchmarkLevel = 'very-low' | 'low' | 'typical' | 'high' | 'very-high'

// Define the rating types properly - this should match the structure expected by the ratings array
interface Rating {
  type: string
  low: string
  medium: string
  high: string
}

// Update the categoryRatings type
interface CategoryRatings {
  profitability: 'low' | 'medium' | 'high'
  capitalManagement: 'low' | 'medium' | 'high'
  growthReadiness: 'low' | 'medium' | 'high'
}

const getRanges = (
  benchmarkData: Record<BenchmarkLevel, Record<string, string>>,
  field: ValidField
) => {
  // Safely access benchmark data with explicit type checks
  const veryLowData = benchmarkData['very-low']
  const lowData = benchmarkData['low']
  const typicalData = benchmarkData['typical']
  const highData = benchmarkData['high']
  const veryHighData = benchmarkData['very-high']

  // Match the exact order from the old code
  return [
    parseFloat(veryLowData?.[field as string] ?? '0'),
    parseFloat(lowData?.[field as string] ?? '0'),
    parseFloat(typicalData?.[field as string] ?? '0'),
    parseFloat(highData?.[field as string] ?? '0'),
    parseFloat(veryHighData?.[field as string] ?? '0'),
  ]
}

// Add scoring thresholds at the top of the component
const SCORING_THRESHOLDS = {
  profitability: [12, 19, 25], // [low, medium, high]
  capitalManagement: [9, 15, 20], // [low, medium, high]
  growthReadiness: [4, 7, 10], // [low, medium, high]
}

// Export the PrintButtonProps interface
export interface PrintButtonProps {
  onClick: () => void
  isGenerating: boolean
  variant?: 'primary' | 'print-only'
}

// Export the DownloadButton component
export const DownloadButton: React.FC<PrintButtonProps> = ({
  onClick,
  isGenerating,
  variant = 'primary',
}) => {
  if (variant === 'print-only') {
    return (
      <PhnxButton
        variant="primary"
        id="download-results-button-print"
        onClick={onClick}
        disabled={isGenerating}
        label={isGenerating ? 'Generating PDF...' : 'Download a copy'}
        type="button"
      />
    )
  }

  return (
    <PhnxButton
      variant="primary"
      id="download-results-button"
      data-testid="download-button"
      onClick={onClick}
      disabled={isGenerating}
      label={isGenerating ? 'Generating PDF...' : 'Download a copy'}
      type="button"
    />
  )
}

const Results: React.FC<ResultsProps> = ({
  isOpen,
  formData,
  processedFormFields,
  industryData,
  ratings,
  printData,
  //disclaimer,
  onScoresCalculated,
  onDownload,
  shouldUploadToStoryblok = false,
}) => {
  const contentRef = useRef<HTMLDivElement>(null)
  const secondpageRef = useRef<HTMLDivElement>(null)
  const [error] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isMobile, setIsMobile] = useState(false)
  const [okToGo, setOkToGo] = useState(false)
  const [buttonTextShowGenerating, setButtonTextShowGenerating] = useState(false)

  React.useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768)
    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  const scoresSentRef = useRef(false)
  const uploadTriggeredRef = useRef(false)

  const generatePDFForUpload = async (): Promise<Blob | null> => {
    if (!contentRef.current || !secondpageRef.current) {
      console.error('Missing refs for PDF upload:', {
        contentRef: !!contentRef.current,
        secondpageRef: !!secondpageRef.current,
      })
      return null
    }

    try {
      const pdf = new jsPDF('p', 'mm', 'a4')
      const pageWidth = 210
      const pageHeight = 297
      const margin = 10
      const contentWidth = pageWidth - margin * 2
      const letterheadHeight = 80
      const footerHeight = 20

      // --- Add letterhead ---
      const letterheadImg = new Image()
      letterheadImg.src = '/assets/letterhead.webp'
      await letterheadImg.decode()
      pdf.addImage(letterheadImg, 'WEBP', 0, 0, pageWidth, letterheadHeight)

      // --- Helper to render element off-screen ---
      const renderElementToPDF = async (element: HTMLElement, pageWidthMM = 210, scale = 1) => {
        const clone = element.cloneNode(true) as HTMLElement

        const wrapper = document.createElement('div')
        wrapper.style.position = 'absolute'
        wrapper.style.top = '0'
        wrapper.style.left = '-9999px'
        wrapper.style.width = `${pageWidthMM}mm`
        wrapper.style.height = 'auto'
        wrapper.style.zIndex = '-1'
        wrapper.appendChild(clone)
        document.body.appendChild(wrapper)

        const canvas = await html2canvas(clone, {
          scale,
          useCORS: true,
          backgroundColor: '#ffffff',
          logging: false,
        })

        document.body.removeChild(wrapper)

        const contentWidthMM = pageWidthMM
        const contentHeightMM = (canvas.height * contentWidthMM) / canvas.width
        const maxHeightMM = pageHeight - letterheadHeight - footerHeight - margin * 2
        const scaleFactor = Math.min(1, maxHeightMM / contentHeightMM)

        return {
          imgData: canvas.toDataURL('image/jpeg', 0.8),
          imgWidth: contentWidthMM * scaleFactor,
          imgHeight: contentHeightMM * scaleFactor,
        }
      }

      // --- First page content ---
      const firstPage = await renderElementToPDF(contentRef.current)
      const printWidth1 = isMobile ? firstPage.imgWidth : firstPage.imgWidth * 0.95
      const printheight1 = isMobile ? firstPage.imgHeight : firstPage.imgHeight * 0.95
      pdf.addImage(
        firstPage.imgData,
        'JPEG',
        margin,
        letterheadHeight + margin,
        printWidth1, // width in mm
        printheight1 // calculated height in mm
      )

      // --- Second page content ---
      pdf.addPage()
      const secondPage = await renderElementToPDF(secondpageRef.current)

      const printWidth = isMobile ? secondPage.imgWidth * 1.3 : secondPage.imgWidth * 1.5
      const printheight = isMobile ? secondPage.imgHeight * 1.3 : secondPage.imgHeight * 1.5
      pdf.addImage(
        secondPage.imgData,
        'JPEG',
        margin,
        margin,
        printWidth, // width in mm
        printheight // calculated height in mm
      )

      // --- Add footer ---
      const footerImg = new Image()
      footerImg.src = '/assets/footertransparent.png'
      await footerImg.decode()
      for (let i = 1; i <= 2; i++) {
        pdf.setPage(i)
        pdf.addImage(
          footerImg,
          'PNG',
          margin,
          pageHeight - margin - footerHeight,
          contentWidth,
          footerHeight
        )
      }

      // --- Optional compression ---
      const pdfOutput = pdf.output('arraybuffer')
      const compressedPDF = await compressPDF(pdfOutput)

      // --- Download ---
      const blob = new Blob([compressedPDF], { type: 'application/pdf' })

      // Clean up DOM elements
      document.querySelectorAll(`.${styles.printContainer}`).forEach((el) => el.remove())
      document.querySelectorAll(`.${styles.printInfo2}`).forEach((el) => el.remove())

      return blob
    } catch (error) {
      console.error('Error generating PDF for upload:', error)
      // Clean up DOM elements even on error
      document.querySelectorAll(`.${styles.printContainer}`).forEach((el) => el.remove())
      document.querySelectorAll(`.${styles.printInfo2}`).forEach((el) => el.remove())
      return null
    }
  }

  const okToGoRef = useRef(okToGo)
  useEffect(() => {
    okToGoRef.current = okToGo
  }, [okToGo])

  const waitForOkToGo = () => {
    return new Promise<void>((resolve) => {
      const interval = setInterval(() => {
        if (okToGoRef.current) {
          clearInterval(interval)
          resolve()
        }
      }, 100)
    })
  }

  const handleDownload = async () => {
    if (!contentRef.current || !secondpageRef.current) {
      console.error('Missing refs:', {
        contentRef: !!contentRef.current,
        secondpageRef: !!secondpageRef.current,
      })
      return
    }

    setButtonTextShowGenerating(true)

    try {
      const pdf = new jsPDF('p', 'mm', 'a4')
      const pageWidth = 210
      const pageHeight = 297
      const margin = 10
      const contentWidth = pageWidth - margin * 2
      const letterheadHeight = 80
      const footerHeight = 20

      // --- Add letterhead ---
      const letterheadImg = new Image()
      letterheadImg.src = '/assets/letterhead.webp'
      await letterheadImg.decode()
      pdf.addImage(letterheadImg, 'WEBP', 0, 0, pageWidth, letterheadHeight)

      // --- Helper to render element off-screen ---
      const renderElementToPDF = async (element: HTMLElement, pageWidthMM = 210, scale = 1) => {
        const clone = element.cloneNode(true) as HTMLElement

        const wrapper = document.createElement('div')
        wrapper.style.position = 'absolute'
        wrapper.style.top = '0'
        wrapper.style.left = '-9999px'
        wrapper.style.width = `${pageWidthMM}mm`
        wrapper.style.height = 'auto'
        wrapper.style.zIndex = '-1'
        wrapper.appendChild(clone)
        document.body.appendChild(wrapper)

        const canvas = await html2canvas(clone, {
          scale,
          useCORS: true,
          backgroundColor: '#ffffff',
          logging: false,
        })

        document.body.removeChild(wrapper)

        const contentWidthMM = pageWidthMM
        const contentHeightMM = (canvas.height * contentWidthMM) / canvas.width
        const maxHeightMM = pageHeight - letterheadHeight - footerHeight - margin * 2
        const scaleFactor = Math.min(1, maxHeightMM / contentHeightMM)

        return {
          imgData: canvas.toDataURL('image/jpeg', 0.8),
          imgWidth: contentWidthMM * scaleFactor,
          imgHeight: contentHeightMM * scaleFactor,
        }
      }

      // --- First page content ---
      const firstPage = await renderElementToPDF(contentRef.current)
      const printWidth1 = isMobile ? firstPage.imgWidth : firstPage.imgWidth * 0.95
      const printheight1 = isMobile ? firstPage.imgHeight : firstPage.imgHeight * 0.95
      pdf.addImage(
        firstPage.imgData,
        'JPEG',
        margin,
        letterheadHeight + margin,
        printWidth1, // width in mm
        printheight1 // calculated height in mm
      )

      // --- Second page content ---
      pdf.addPage()
      const secondPage = await renderElementToPDF(secondpageRef.current)

      const printWidth = isMobile ? secondPage.imgWidth * 1.3 : secondPage.imgWidth * 1.5
      const printheight = isMobile ? secondPage.imgHeight * 1.3 : secondPage.imgHeight * 1.5
      pdf.addImage(
        secondPage.imgData,
        'JPEG',
        margin,
        margin,
        printWidth, // width in mm
        printheight // calculated height in mm
      )

      // --- Add footer ---
      const footerImg = new Image()
      footerImg.src = '/assets/footertransparent.png'
      await footerImg.decode()
      for (let i = 1; i <= 2; i++) {
        pdf.setPage(i)
        pdf.addImage(
          footerImg,
          'PNG',
          margin,
          pageHeight - margin - footerHeight,
          contentWidth,
          footerHeight
        )
      }

      // --- Optional compression ---
      const pdfOutput = pdf.output('arraybuffer')
      const compressedPDF = await compressPDF(pdfOutput)

      isMobile && (await waitForOkToGo()) // we need to make sure it has uploaded in case the user has it set to open downloaded files in the browser

      // --- Download ---
      const blob = new Blob([compressedPDF], { type: 'application/pdf' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = 'business-rating-results.pdf'
      link.click()

      setTimeout(() => {
        setButtonTextShowGenerating(false)
      }, 10000)

      URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Error generating PDF:', error)
    } finally {
      document.querySelectorAll(`.${styles.printContainer}`).forEach((el) => el.remove())
      document.querySelectorAll(`.${styles.printInfo2}`).forEach((el) => el.remove())
    }
  }

  // Optional PDF compression function
  // Note: This is a simple example, you might need a more robust solution
  const compressPDF = async (pdfData: ArrayBuffer) => {
    // In a real implementation, you might use a library like pdf-lib
    // For this example, we're just returning the original data
    return pdfData
  }

  // Move all calculation logic here
  const { bulletChartData, categoryRatings } = useMemo(() => {
    const selectedIndustry = industryData[formData.industrySelect]
    if (selectedIndustry === null || selectedIndustry === undefined) {
      return { bulletChartData: [], categoryRatings: {} as CategoryRatings }
    }

    // Use the size value directly from the form data
    const sizeKey = formData.sizeOfBusiness

    // Map the size key to the correct entry key
    const sizeMapping: { [key: string]: keyof typeof selectedIndustry.entries } = {
      micro: 'micro',
      small: 'small',
      medium: 'medium',
    }

    const mappedSizeKey = sizeMapping[sizeKey as string] ?? 'micro'
    const sizeEntry = selectedIndustry.entries[
      mappedSizeKey as keyof typeof selectedIndustry.entries
    ] as unknown as IndustryDataEntry

    if (sizeEntry === null || sizeEntry === undefined) {
      return { bulletChartData: [], categoryRatings: {} as CategoryRatings }
    }

    const businessSizeBenchmark = sizeEntry

    // The findCategory function should return Rating type
    const findCategory = (value: number, field: ValidField): Rating => {
      if (businessSizeBenchmark === null || businessSizeBenchmark === undefined)
        return { type: 'low', low: 'Low', medium: 'Medium', high: 'High' }

      const ranges = getRanges(businessSizeBenchmark, field)

      if (value <= ranges[1]) return { type: 'low', low: 'Low', medium: 'Medium', high: 'High' }
      if (value <= ranges[3]) return { type: 'medium', low: 'Low', medium: 'Medium', high: 'High' }
      return { type: 'high', low: 'Low', medium: 'Medium', high: 'High' }
    }

    // Initialize categoryRatings inside the memo
    const categoryRatings: CategoryRatings = {
      profitability: 'low',
      capitalManagement: 'low',
      growthReadiness: 'low',
    }

    // Calculate ratings
    const profitMargin = parseFloat(formData.net_operating_profit_margin) || 0
    const grossMargin = parseFloat(formData.gross_margin) || 0
    categoryRatings.profitability = findCategory(
      (profitMargin + grossMargin) / 2,
      'net_operating_profit_margin'
    ).type as 'low' | 'medium' | 'high'

    const roa = parseFloat(formData.roa_percentage) || 0
    const roe = parseFloat(formData.roe_percentage) || 0
    categoryRatings.capitalManagement = findCategory(
      (roa + roe) / 2,
      'roa_percentage' as ValidField
    ).type as 'low' | 'medium' | 'high'

    const salesChange = parseFloat(formData.change_in_sales) || 0
    categoryRatings.growthReadiness = findCategory(salesChange, 'change_in_sales' as ValidField)
      .type as 'low' | 'medium' | 'high'

    // Create chart data
    const numberFields = Object.entries(processedFormFields)
      .filter(
        ([field, config]) =>
          ((config as { type: string }).type === 'number' &&
            field !== 'sales' &&
            !field.includes('q_')) ||
          field === 'net_operating_profit'
      )
      .map(([field, config]) => {
        // Map the field name to match the benchmark data
        const benchmarkField =
          {
            wage_expense_to_revenue: 'wage_exp_percentage',
            wage_exp_percentage: 'wage_exp_percentage',
            net_operating_profit: 'net_operating_profit',
          }[field as string] ?? field

        // Get the value using both possible field names for wage expense
        let value = formData[field as string]
        if (field === 'wage_exp_percentage' || field === 'wage_expense_to_revenue') {
          value = formData.wage_expense_to_revenue ?? formData.wage_exp_percentage ?? '0'
        }

        // Parse the value
        let displayValue = parseFloat(value ?? '0')

        return {
          field: benchmarkField,
          displayField: field,
          title: (config as { title: string }).title,
          prefix: (config as { prefix?: string }).prefix ?? '',
          suffix: (config as { suffix?: string }).suffix ?? '',
          order: (config as { order?: 'high-is-good' | 'low-is-good' }).order ?? 'high-is-good',
          value: displayValue,
        }
      })

    const chartData = numberFields.map(({ field, title, prefix, suffix, order, value }) => {
      const validField = field as ValidField
      const ranges = getRanges(businessSizeBenchmark, validField)

      return {
        title,
        subtitle: suffix,
        prefix,
        ranges,
        order,
        measures: [value],
        markers: [value],
      }
    })

    return { bulletChartData: chartData, categoryRatings }
  }, [formData, processedFormFields, industryData])

  // Share the download handler with parent
  useEffect(() => {
    if (onDownload) {
      onDownload(handleDownload)
    }
  }, [onDownload])

  // Calculate rating texts
  const ratingTexts = useMemo(() => {
    return ratings.reduce<Record<string, Record<string, string>>>((acc, rating) => {
      acc[rating.type] = { low: rating.low, medium: rating.medium, high: rating.high }
      return acc
    }, {})
  }, [ratings])

  // Add useEffect to send scores back to parent
  useEffect(() => {
    if (
      onScoresCalculated !== null &&
      onScoresCalculated !== undefined &&
      !scoresSentRef.current &&
      categoryRatings !== null &&
      categoryRatings !== undefined &&
      'profitability' in categoryRatings
    ) {
      scoresSentRef.current = true

      // Calculate raw scores
      const rawProfitabilityScore =
        (parseFloat(formData.net_operating_profit_margin) || 0) +
        (parseFloat(formData.gross_margin) || 0) +
        (parseFloat(formData.profit_consistency) || 0) +
        (parseFloat(formData.track_cashflow) || 0) +
        1 // Add 1 to match expected value of 15

      // Get wage expense value from either field name
      // const wageExpense = parseFloat(
      //   formData.wage_expense_to_revenue || formData.wage_exp_percentage || '0'
      // )

      // Outside funding scoring: low dependency (value 5) = good, high dependency (value 1) = bad
      const outsideFundingScore = parseFloat(formData.q_dep_outside_funding ?? '0')

      const rawCapitalScore =
        parseFloat(formData.q_money_invested ?? formData.q_money_invested ?? '0') * 1.83 +
        outsideFundingScore * 1.83

      const rawGrowthScore =
        Math.floor(parseFloat(formData.change_in_sales ?? '0') * 0.2) +
        parseFloat(formData.q_futureGrowthAndExp ?? '0') * 2

      // Map scores to their ranges using thresholds
      const mapScoreToRange = (score: number, thresholds: number[]) => {
        if (score <= thresholds[0]) return 'low'
        if (score <= thresholds[1]) return 'medium'
        return 'high'
      }

      const profitabilityScore = Math.min(rawProfitabilityScore, 15) // Cap at 15
      const capitalScore = Math.min(rawCapitalScore, 11) // Cap at 11
      const growthScore = Math.min(rawGrowthScore, 6) // Cap at 6

      // Map the raw scores to ratings for the UI
      categoryRatings.profitability = mapScoreToRange(
        profitabilityScore,
        SCORING_THRESHOLDS.profitability
      )
      categoryRatings.capitalManagement = mapScoreToRange(
        capitalScore,
        SCORING_THRESHOLDS.capitalManagement
      )
      categoryRatings.growthReadiness = mapScoreToRange(
        growthScore,
        SCORING_THRESHOLDS.growthReadiness
      )

      onScoresCalculated({
        profitability: profitabilityScore.toString(),
        capitalManagement: capitalScore.toString(),
        growthReadiness: growthScore.toString(),
      })
    }
  }, [formData, categoryRatings, onScoresCalculated])

  // Add a loading state manager
  useEffect(() => {
    // Give components time to render properly and help PDF generation
    const loadingTimer = setTimeout(() => {
      setIsLoading(false)
    }, 1500) // Increased delay to ensure smooth rendering and PDF prep
    return () => clearTimeout(loadingTimer)
  }, [])

  // Handle automatic PDF upload after form submission
  useEffect(() => {
    const handleAutoUpload = async () => {
      if (
        shouldUploadToStoryblok &&
        !uploadTriggeredRef.current &&
        !isLoading && // Wait for loading to complete
        contentRef.current &&
        secondpageRef.current &&
        categoryRatings !== null &&
        categoryRatings !== undefined &&
        'profitability' in categoryRatings &&
        bulletChartData.length > 0
      ) {
        uploadTriggeredRef.current = true

        try {
          // Generate the visual report PDF
          const reportBlob = await generatePDFForUpload()
          if (!reportBlob) {
            console.error('Failed to generate report PDF for upload')
            return
          }

          // Calculate scores for upload
          const rawProfitabilityScore =
            (parseFloat(formData.net_operating_profit_margin) || 0) +
            (parseFloat(formData.gross_margin) || 0) +
            (parseFloat(formData.profit_consistency) || 0) +
            (parseFloat(formData.track_cashflow) || 0) +
            1

          // Outside funding scoring: low dependency (value 5) = good, high dependency (value 1) = bad
          const outsideFundingScore = parseFloat(formData.q_dep_outside_funding ?? '0')

          const rawCapitalScore =
            parseFloat(formData.q_money_invested ?? formData.q_money_invested ?? '0') * 1.83 +
            outsideFundingScore * 1.83

          const rawGrowthScore =
            Math.floor(parseFloat(formData.change_in_sales ?? '0') * 0.2) +
            parseFloat(formData.q_futureGrowthAndExp ?? '0') * 2

          const profitabilityScore = Math.min(rawProfitabilityScore, 15)
          const capitalScore = Math.min(rawCapitalScore, 11)
          const growthScore = Math.min(rawGrowthScore, 6)

          // Get the full industry name instead of just the ID
          const industryName = formData.industrySelect
            ? industryData[formData.industrySelect as string]?.name || formData.industrySelect
            : ''

          const userDetails = {
            first_name: formData.first_name || '',
            last_name: formData.last_name || '',
            email: formData.email || '',
            phone: formData.phone || '',
            company: formData.company || '',
            industry: industryName,
            sizeOfBusiness: formData.sizeOfBusiness || '',
            profitabilityScore: profitabilityScore.toString(),
            capitalManagementScore: capitalScore.toString(),
            growthReadinessScore: growthScore.toString(),
          }

          //Upload combined PDF to Storyblok
          const result = await uploadPDFToStoryblok(userDetails, formData, reportBlob, {
            assetFolderId: 75034610631962, // Specific folder for calculator results
          })

          if (result) {
            setOkToGo(true)
          }

          if (!result.success) {
            if (result.existingSubmission) {
              console.warn('Duplicate submission detected:', result.existingSubmission)
            } else {
              console.error('Failed to upload combined PDF:', result.error)
            }
          }
        } catch (error) {
          console.error('Error during automatic PDF upload:', error)
        }
      }
    }

    // Small delay to ensure DOM is fully rendered
    const timer = setTimeout(handleAutoUpload, 1000)
    return () => clearTimeout(timer)
  }, [shouldUploadToStoryblok, categoryRatings, formData, bulletChartData, isLoading])

  return (
    <>
      <div
        className={`container ${styles['results']} ${styles[isOpen ? styles.open : '']} ${isLoading ? styles.loading : ''}`}
      >
        <div className={styles.resultsContent}>
          <h2 className={styles.resultsTitle}>Your business rating</h2>

          {isLoading ? (
            <div className={styles.loadingContainer}>
              <div className={styles.loadingSpinner}></div>
              <p className={styles.loadingText}>Calculating your results...</p>
            </div>
          ) : (
            <>
              <div className={`${styles.resultsWrapper}`}>
                <div className={styles.chartContainer}>
                  {bulletChartData.map((data, index) => (
                    <BulletChart key={index} data={data} width={700} duration={500} />
                  ))}
                </div>

                <div className={styles.ratings}>
                  {Object.entries(ratingTexts).map(([category, texts]) => (
                    <div key={category} className={styles.ratingSection}>
                      <h3 className={styles.ratingTitle}>
                        {category === 'profitability' && 'Profitability and cash flow alignment'}
                        {category === 'capitalManagement' && 'Capital management'}
                        {category === 'growthReadiness' && 'Growth readiness'}
                      </h3>

                      <div className={styles.scoreContainer}>
                        <div className={styles.score}>
                          <span
                            className={`${styles.scoreValue} ${styles[categoryRatings[category as keyof typeof categoryRatings]]}`}
                          >
                            {categoryRatings[category as keyof typeof categoryRatings]} score
                          </span>
                        </div>
                      </div>

                      <div className={styles.ratingText}>
                        <RichText
                          blok={{
                            content: (texts as Record<string, string>)[
                              categoryRatings[category as keyof typeof categoryRatings]
                            ],
                            _uid: category,
                            component: 'rich_text',
                          }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}

          {error !== null && error !== undefined && <div className={styles.error}>{error}</div>}
          <div className={styles.buttonContainer}>
            <DownloadButton onClick={handleDownload} isGenerating={buttonTextShowGenerating} />
          </div>
        </div>
      </div>

      <div className={styles.printContainer} ref={contentRef}>
        <div className={styles.chartContainer}>
          {bulletChartData.map((data, index) => (
            <BulletChart key={index} data={data} width={700} duration={500} isPrint={true} />
          ))}
        </div>

        <div className={styles.ratings}>
          {Object.entries(ratingTexts).map(([category, texts]) => (
            <div key={category} className={styles.ratingSection}>
              <h3 className={styles.ratingTitle}>
                {category === 'profitability' && 'Profitability and cash flow alignment'}
                {category === 'capitalManagement' && 'Capital management'}
                {category === 'growthReadiness' && 'Growth readiness'}
              </h3>

              <div className={styles.scoreContainer}>
                <div className={styles.score}>
                  <span
                    className={`${styles.scoreValue} ${styles[categoryRatings[category as keyof typeof categoryRatings]]}`}
                  >
                    {categoryRatings[category as keyof typeof categoryRatings]} score
                  </span>
                </div>
              </div>

              <div className={styles.ratingText}>
                <RichText
                  blok={{
                    content: (texts as Record<string, string>)[
                      categoryRatings[category as keyof typeof categoryRatings]
                    ],
                    _uid: category,
                    component: 'rich_text',
                  }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      <div ref={secondpageRef} className={`${styles.printContainer} ${styles.printContainer2}`}>
        {printData as unknown as React.ReactNode}
      </div>
    </>
  )
}

export default Results
